import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox, CalendarClock, Trash2 } from "lucide-react";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: "border-gold/60 bg-gold/10 text-gold-muted",
  read: "border-obsidian/15 bg-alabaster text-slate-500",
  replied: "border-emerald-800/25 bg-emerald-50 text-emerald-900",
};

const StatusBadge = ({ status }) => (
  <span
    data-testid={`inquiry-status-${status}`}
    className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLES[status] || STATUS_STYLES.new}`}
  >
    {status || "new"}
  </span>
);

const InquiryCard = ({ inquiry, index, onStatus }) => {
  const status = inquiry.status || "new";
  return (
    <div data-testid={`inquiry-card-${index}`} className="rounded-xl border border-obsidian/10 bg-white p-7">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-serif text-xl text-obsidian">{inquiry.name}</p>
            <StatusBadge status={status} />
            {inquiry.track && (
              <span
                data-testid={`inquiry-track-${index}`}
                className="rounded-full border border-obsidian/15 bg-obsidian px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-gold"
              >
                {inquiry.track === "personal" ? "Personal" : "Business"}
              </span>
            )}
          </div>
          {inquiry.company && <p className="mt-1 text-sm text-slate-500">{inquiry.company}</p>}
        </div>
        <p className="font-mono text-xs text-slate-400">
          {inquiry.created_at ? new Date(inquiry.created_at).toLocaleString() : ""}
        </p>
      </div>
      <div className="mt-5 grid gap-x-8 gap-y-2 border-t border-obsidian/10 pt-5 text-sm sm:grid-cols-2">
        <p className="text-slate-600"><span className="text-slate-400">Email:</span> {inquiry.email}</p>
        {inquiry.phone && <p className="text-slate-600"><span className="text-slate-400">Phone:</span> {inquiry.phone}</p>}
        <p className="text-slate-600"><span className="text-slate-400">Country:</span> {inquiry.country || "—"}</p>
        {inquiry.lang && (
          <p data-testid={`inquiry-lang-${index}`} className="text-slate-600"><span className="text-slate-400">Language:</span> {{ en: "English", uk: "Ukrainian", ru: "Russian", ar: "Arabic" }[inquiry.lang] || inquiry.lang}</p>
        )}
        <p className="text-slate-600"><span className="text-slate-400">Support:</span> {inquiry.support_type || "—"}</p>
        <p className="text-slate-600"><span className="text-slate-400">Hours/week:</span> {inquiry.hours_per_week || "—"}</p>
        <p className="text-slate-600"><span className="text-slate-400">Preferred contact:</span> {inquiry.preferred_contact || "—"}</p>
      </div>
      <p className="mt-4 rounded-lg bg-alabaster p-4 text-sm leading-relaxed text-slate-700">{inquiry.description}</p>
      <div className="mt-5 flex flex-wrap gap-3">
        {status !== "read" && (
          <button
            data-testid={`inquiry-mark-read-${index}`}
            onClick={() => onStatus(inquiry.id, "read")}
            className="rounded-full border border-obsidian/15 px-5 py-2 text-xs font-semibold text-obsidian transition-colors hover:border-gold-muted hover:text-gold-muted"
          >
            Mark as Read
          </button>
        )}
        {status !== "replied" && (
          <button
            data-testid={`inquiry-mark-replied-${index}`}
            onClick={() => onStatus(inquiry.id, "replied")}
            className="rounded-full border border-obsidian/15 px-5 py-2 text-xs font-semibold text-obsidian transition-colors hover:border-gold-muted hover:text-gold-muted"
          >
            Mark as Replied
          </button>
        )}
        {status !== "new" && (
          <button
            data-testid={`inquiry-mark-new-${index}`}
            onClick={() => onStatus(inquiry.id, "new")}
            className="rounded-full border border-obsidian/10 px-5 py-2 text-xs text-slate-400 transition-colors hover:text-slate-600"
          >
            Move back to New
          </button>
        )}
      </div>
    </div>
  );
};

const ConsultationForm = ({ onCreated }) => {
  const [form, setForm] = useState({ client_name: "", client_email: "", scheduled_at: "", note: "" });
  const [saving, setSaving] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API}/consultations`,
        { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() },
        { withCredentials: true }
      );
      toast.success("Consultation added — reminder email is scheduled for 24 hours before the call.");
      setForm({ client_name: "", client_email: "", scheduled_at: "", note: "" });
      onCreated();
    } catch (err) {
      toast.error("Could not add the consultation. Please check the details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      data-testid="consultation-form"
      onSubmit={onSubmit}
      className="rounded-xl border border-obsidian/10 bg-white p-7"
    >
      <p className="font-serif text-xl text-obsidian">Add a Scheduled Consultation</p>
      <p className="mt-1 text-sm text-slate-500">
        The client automatically receives a reminder email 24 hours before the call.
      </p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <input
          data-testid="consultation-name-input"
          name="client_name"
          required
          value={form.client_name}
          onChange={onChange}
          className="input-field"
          placeholder="Client name"
        />
        <input
          data-testid="consultation-email-input"
          type="email"
          name="client_email"
          required
          value={form.client_email}
          onChange={onChange}
          className="input-field"
          placeholder="Client email"
        />
        <input
          data-testid="consultation-datetime-input"
          type="datetime-local"
          name="scheduled_at"
          required
          value={form.scheduled_at}
          onChange={onChange}
          className="input-field"
        />
        <input
          data-testid="consultation-note-input"
          name="note"
          value={form.note}
          onChange={onChange}
          className="input-field"
          placeholder="Note (optional — included in the reminder)"
        />
      </div>
      <button
        data-testid="consultation-add-button"
        type="submit"
        disabled={saving}
        className="btn-gold mt-6 !px-6 !py-3 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {saving ? "Adding…" : "Add Consultation"}
      </button>
    </form>
  );
};

const ConsultationCard = ({ consultation, index, onDelete }) => (
  <div
    data-testid={`consultation-card-${index}`}
    className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-obsidian/10 bg-white p-6"
  >
    <div>
      <p className="font-serif text-lg text-obsidian">{consultation.client_name}</p>
      <p className="text-sm text-slate-500">{consultation.client_email}</p>
      {consultation.note && <p className="mt-1 text-xs italic text-slate-400">{consultation.note}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-4">
      <div className="text-right">
        <p className="font-mono text-sm text-obsidian">
          {new Date(consultation.scheduled_at).toLocaleString()}
        </p>
        <p
          data-testid={`consultation-reminder-${index}`}
          className={`mt-1 font-mono text-[10px] uppercase tracking-[0.15em] ${consultation.reminder_sent ? "text-emerald-800" : "text-gold-muted"}`}
        >
          {consultation.reminder_sent ? "Reminder sent" : "Reminder scheduled · 24h before"}
        </p>
      </div>
      <button
        data-testid={`consultation-delete-${index}`}
        onClick={() => onDelete(consultation.id)}
        className="rounded-full border border-obsidian/10 p-2.5 text-slate-400 transition-colors hover:border-red-300 hover:text-red-500"
        aria-label="Delete consultation"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(me.data);
      const [inq, con] = await Promise.all([
        axios.get(`${API}/inquiries`, { withCredentials: true }),
        axios.get(`${API}/consultations`, { withCredentials: true }),
      ]);
      setInquiries(inq.data);
      setConsultations(con.data);
    } catch (err) {
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}/status`, { status }, { withCredentials: true });
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch (err) {
      toast.error("Could not update the status.");
    }
  };

  const deleteConsultation = async (id) => {
    try {
      await axios.delete(`${API}/consultations/${id}`, { withCredentials: true });
      setConsultations((prev) => prev.filter((c) => c.id !== id));
      toast.success("Consultation removed.");
    } catch (err) {
      toast.error("Could not remove the consultation.");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      navigate("/admin/login");
    }
  };

  const newCount = inquiries.filter((i) => (i.status || "new") === "new").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-alabaster pb-24 pt-32">
      <SEO title="Owner Dashboard | VCK Business Lab" description="Private owner dashboard." path="/admin" noindex />
      <div className="mx-auto max-w-5xl px-6 sm:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="overline text-gold-muted">Owner Dashboard</p>
            <h1 className="mt-3 font-serif text-4xl text-obsidian">Welcome back.</h1>
            {user && <p className="mt-2 text-sm text-slate-500">Signed in as {user.email}</p>}
          </div>
          <div className="flex gap-3">
            <button data-testid="admin-refresh-button" onClick={load} className="btn-outline-dark !px-5 !py-2.5">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className="btn-outline-dark !px-5 !py-2.5">
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>

        <div className="mt-10 flex gap-3 border-b border-obsidian/10 pb-0">
          <button
            data-testid="admin-tab-inquiries"
            onClick={() => setTab("inquiries")}
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
              tab === "inquiries" ? "border-gold text-obsidian" : "border-transparent text-slate-400 hover:text-obsidian"
            }`}
          >
            <Inbox className="h-4 w-4" /> Inquiries
            {newCount > 0 && (
              <span data-testid="inquiries-new-count" className="rounded-full bg-gold px-2.5 py-0.5 font-mono text-[10px] text-obsidian">
                {newCount} new
              </span>
            )}
          </button>
          <button
            data-testid="admin-tab-consultations"
            onClick={() => setTab("consultations")}
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
              tab === "consultations" ? "border-gold text-obsidian" : "border-transparent text-slate-400 hover:text-obsidian"
            }`}
          >
            <CalendarClock className="h-4 w-4" /> Consultations
          </button>
        </div>

        {tab === "inquiries" && (
          <div className="mt-10 space-y-6" data-testid="inquiries-panel">
            {loading ? (
              <p data-testid="inquiries-loading" className="py-16 text-center text-sm text-slate-400">Loading…</p>
            ) : inquiries.length === 0 ? (
              <div data-testid="inquiries-empty" className="rounded-xl border border-obsidian/10 bg-white py-20 text-center">
                <Inbox className="mx-auto h-8 w-8 text-gold-muted" />
                <p className="mt-4 font-serif text-xl text-obsidian">No inquiries yet.</p>
                <p className="mt-2 text-sm text-slate-500">New contact form submissions will appear here.</p>
              </div>
            ) : (
              inquiries.map((inq, i) => <InquiryCard key={inq.id || i} inquiry={inq} index={i} onStatus={setStatus} />)
            )}
          </div>
        )}

        {tab === "consultations" && (
          <div className="mt-10 space-y-6" data-testid="consultations-panel">
            <ConsultationForm onCreated={load} />
            {consultations.length === 0 && !loading ? (
              <div data-testid="consultations-empty" className="rounded-xl border border-obsidian/10 bg-white py-16 text-center">
                <CalendarClock className="mx-auto h-8 w-8 text-gold-muted" />
                <p className="mt-4 font-serif text-xl text-obsidian">No consultations scheduled.</p>
                <p className="mt-2 text-sm text-slate-500">Add a booking above and the client will be reminded automatically.</p>
              </div>
            ) : (
              consultations.map((c, i) => (
                <ConsultationCard key={c.id || i} consultation={c} index={i} onDelete={deleteConsultation} />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
